import { type FormEvent, useEffect, useState } from "react"
import { toast } from "sonner"
import { type Certificate, type CurrentUser, api, formatDate } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { KeyRound, Plus, Trash2, Upload } from "lucide-react"

type Props = { currentUser: CurrentUser }

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(typeof r.result === "string" ? r.result : "")
    r.onerror = () => reject(new Error("Falha ao ler o arquivo."))
    r.readAsText(file)
  })
}

export function CertificatesPage({ currentUser }: Props) {
  const [certs, setCerts] = useState<Certificate[]>([])
  const [loading, setLoading] = useState(true)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [creating, setCreating] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [label, setLabel] = useState("")
  const [keyFile, setKeyFile] = useState<File | null>(null)

  const isAdmin = currentUser.permissions.includes("admin")

  useEffect(() => {
    api
      .getCertificates()
      .then((r) => setCerts(r.certificates))
      .catch(() => toast.error("Erro ao carregar certificados."))
      .finally(() => setLoading(false))
  }, [])

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    if (!label.trim()) return
    if (!keyFile) return
    setCreating(true)
    try {
      const keyPem = await readFileAsText(keyFile)
      const res = await api.createCertificate({
        label: label.trim(),
        keyPem,
        fileName: keyFile.name,
      })
      setCerts(res.certificates)
      toast.success(res.log || "Certificado adicionado.")
      setLabel("")
      setKeyFile(null)
      setShowForm(false)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao criar certificado."
      )
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async () => {
    if (deleteId === null) return
    setDeleting(true)
    try {
      const res = await api.deleteCertificate(deleteId)
      setCerts(res.certificates)
      toast.success(res.log || "Certificado removido.")
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao remover certificado."
      )
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  const submitDisabled =
    creating ||
    !label.trim() ||
    !keyFile

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto p-4 sm:p-6 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">
            Certificados SSH
          </h1>
          <p className="text-sm text-muted-foreground">
            Chaves privadas PEM usadas no SSH. Cadastre quantas precisar e
            vincule cada uma aos ambientes em Ambientes.
          </p>
        </div>
        {isAdmin && (
          <Button
            size="sm"
            variant={showForm ? "outline" : "default"}
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus />
            {showForm ? "Cancelar" : "Novo certificado"}
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Adicionar certificado
            </CardTitle>
            <CardDescription className="text-xs">
              Envie o arquivo de chave privada (PEM). O conteudo sera salvo no
              SQLite. Depois, escolha este certificado em cada ambiente.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="flex flex-col gap-4">
              <div className="space-y-2">
                <Label htmlFor="cert-label">Rótulo *</Label>
                <Input
                  id="cert-label"
                  placeholder="ex: producao"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={creating}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cert-file">Arquivo de chave privada *</Label>
                <div className="flex flex-wrap items-center gap-3">
                  <Input
                    id="cert-file"
                    type="file"
                    accept=".pem,.key,text/plain,*/*"
                    className="min-w-0 max-w-full flex-1 cursor-pointer font-mono text-xs sm:max-w-xl"
                    disabled={creating}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      setKeyFile(f ?? null)
                    }}
                  />
                  {keyFile ? (
                    <span className="text-xs text-muted-foreground">
                      {keyFile.name} ({Math.ceil(keyFile.size / 1024)} KB)
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={submitDisabled}>
                  <Upload className="size-4 opacity-80" />
                  {creating ? "Salvando..." : "Salvar certificado"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-medium">
                Certificados cadastrados
              </CardTitle>
              <CardDescription className="text-xs">
                {loading ? "Carregando..." : `${certs.length} certificado(s)`}
              </CardDescription>
            </div>
            <KeyRound className="size-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 px-6 pb-4">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : certs.length === 0 ? (
            <div className="flex min-h-48 flex-col items-start gap-3 px-6 py-12 sm:min-h-56">
              <KeyRound className="size-8 text-muted-foreground/40" />
              <p className="w-full text-left text-sm text-muted-foreground">
                Nenhum certificado cadastrado.
              </p>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowForm(true)}
                  className="mt-1"
                >
                  <Plus />
                  Adicionar certificado
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {certs.map((cert) => (
                <div
                  key={cert.id}
                  className="flex items-center justify-between gap-4 px-6 py-4"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-muted/40">
                      <KeyRound className="size-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{cert.label}</span>
                        <Badge
                          variant="secondary"
                          className="font-mono text-[10px]"
                        >
                          id:{cert.id}
                        </Badge>
                        <Badge
                          variant={
                            cert.key_storage === "stored" ? "default" : "outline"
                          }
                          className="text-[10px]"
                        >
                          {cert.key_storage === "stored"
                            ? "Na base"
                            : "Arquivo no servidor"}
                        </Badge>
                      </div>
                      <p className="truncate font-mono text-[11px] text-muted-foreground">
                        {cert.key_path}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Importado em {formatDate(cert.imported_at)}
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="shrink-0 text-destructive hover:text-destructive"
                      onClick={() => setDeleteId(cert.id)}
                    >
                      <Trash2 />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteId !== null}
        onOpenChange={(o) => !o && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover certificado</AlertDialogTitle>
            <AlertDialogDescription>
              Isso removerá o certificado do sistema. Ambientes que dependem
              dele perderão a referência à chave. Essa ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
            >
              {deleting ? "Removendo..." : "Remover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
